import type { FieldSpec, FieldValue } from "./useRowDrafts";

/**
 * One labeled control in the profile editors: text, textarea, select, number,
 * date or checkbox, with the error wired through `aria-invalid` and
 * `aria-describedby` so the validation summary's focus jump lands on a field
 * that explains itself.
 */
export function FieldInput({
  id,
  spec,
  value,
  error,
  onChange,
}: {
  readonly id: string;
  readonly spec: FieldSpec;
  readonly value: FieldValue | undefined;
  readonly error?: string | undefined;
  readonly onChange: (value: FieldValue) => void;
}) {
  const errorId = `${id}-error`;
  const invalid = error !== undefined;
  const describedBy = invalid ? errorId : undefined;

  if (spec.kind === "checkbox") {
    return (
      <label className="pp-check" htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={value === true}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span>{spec.label}</span>
        {invalid ? (
          <span id={errorId} className="pp-field-error">
            {error}
          </span>
        ) : null}
      </label>
    );
  }

  const text = typeof value === "string" ? value : "";
  const className = `pp-field${spec.narrow ? " pp-field-narrow" : ""}${spec.wide ? " pp-field-wide" : ""}`;
  return (
    <label className={className} htmlFor={id}>
      <span>
        {spec.label}
        {spec.required ? <span aria-hidden="true"> *</span> : null}
      </span>
      {spec.kind === "select" ? (
        <select
          id={id}
          value={text}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
        >
          {(spec.options ?? []).map((option) => (
            <option key={option.value || "empty"} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : spec.kind === "textarea" ? (
        <textarea
          id={id}
          rows={2}
          value={text}
          placeholder={spec.placeholder}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input
          id={id}
          type={spec.kind === "number" ? "number" : spec.kind === "date" ? "date" : "text"}
          value={text}
          placeholder={spec.placeholder}
          {...(spec.min === undefined ? {} : { min: spec.min })}
          {...(spec.max === undefined ? {} : { max: spec.max })}
          {...(spec.maxLength === undefined ? {} : { maxLength: spec.maxLength })}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
      {invalid ? (
        <span id={errorId} className="pp-field-error">
          {error}
        </span>
      ) : null}
    </label>
  );
}
